const PATH_BASED_HOST = process.env.NEXT_PUBLIC_PATH_BASED_HOST || 'turno.core.uy'
const CANONICAL_HOST = process.env.NEXT_PUBLIC_CANONICAL_HOST || PATH_BASED_HOST

/**
 * Hosts that use path-based tenant routing (slug extracted from URL path
 * instead of subdomain). On these hosts, the slug is sent as X-Tenant-Slug
 * header so the API can resolve the tenant.
 */
const PATH_BASED_HOSTS = new Set([PATH_BASED_HOST])
const NON_TENANT_PATHS = new Set([
  'admin',
  'agenda',
  'agenda-admin',
  'api',
  'auth',
  'barber',
  'barbers',
  'booking',
  'favicon.ico',
  'favicon.svg',
  'landing',
  'privacy',
  'robots.txt',
  'services',
  'sitemap.xml',
  'studio',
  'turno',
  'uploads',
  'ws',
  '_next',
])

/**
 * Resolve tenant slug from URL path or cookie on path-based hosts.
 */
export function getTenantSlug(): string | null {
  if (typeof window === 'undefined') return null
  if (!PATH_BASED_HOSTS.has(window.location.hostname.toLowerCase())) return null

  // 1) Tenant-prefixed URL paths, including the public home `/<slug>`.
  // The public page cannot rely on the tenant cookie in local HTTP because
  // production cookies are Secure; the URL itself is the reliable source.
  const segments = window.location.pathname.split('/').filter(Boolean)
  if (segments.length > 0 && /^[a-z0-9-]{1,63}$/.test(segments[0]) &&
      !NON_TENANT_PATHS.has(segments[0])) {
    return segments[0]
  }

  // 2) Cookie set by middleware
  const match = document.cookie.match(/(?:^|;\s*)tenant_slug=([^;]+)/)
  if (match) {
    const v = decodeURIComponent(match[1])
    if (/^[a-z0-9-]{1,63}$/.test(v)) return v
  }

  return null
}

/**
 * Build the login URL preserving the tenant slug prefix on path-based hosts
 * (so the address bar shows /<slug>/admin/login).
 */
export function loginUrl(): string {
  if (typeof window === 'undefined') return '/admin/login'
  if (PATH_BASED_HOSTS.has(window.location.hostname.toLowerCase())) {
    const slug = getTenantSlug()
    if (slug) return `/${slug}/admin/login`
  }
  return '/admin/login'
}

/** Build the canonical public tenant URL on the shared path-based host. */
export function tenantPublicUrl(slugOrDomain?: string | null): string | null {
  const suppliedSlug = slugOrDomain?.split('.')[0]
  const slug = suppliedSlug || getTenantSlug()
  if (!slug || !/^[a-z0-9-]{1,63}$/.test(slug)) return null
  return `https://${CANONICAL_HOST}/${slug}`
}

/**
 * Prepend the tenant slug to a public path on path-based hosts.
 * e.g. tenantPath('/booking') → '/brotherhood/booking'
 * Private paths (/admin, /profesional) are NOT prefixed —
 * the middleware handles those separately.
 */
export function tenantPath(path: string): string {
  if (typeof window === 'undefined') return path
  if (!PATH_BASED_HOSTS.has(window.location.hostname.toLowerCase())) return path
  // Don't prefix private paths — middleware handles those
  if (path === '/admin' || path.startsWith('/admin/') ||
      path === '/profesional' || path.startsWith('/profesional/')) {
    return path
  }
  const slug = getTenantSlug()
  if (!slug) return path
  // Avoid double-prefixing
  if (path === `/${slug}` || path.startsWith(`/${slug}/`)) return path
  return path === '/' ? `/${slug}` : `/${slug}${path}`
}
